import { IconNames } from '@dixa/lib-shared-habla';
import { triggerShortcutMenu } from 'articleEditor/plugins/shortcut';
import { makeNodeId } from 'lib-knowledge-document-structure';
import {
  deleteText,
  getLastChildPath,
  getNode,
  insertNodes,
  setNodes,
} from 'lib-knowledge-document-structure/plate/common';
import { isMyElement } from 'lib-knowledge-document-structure/plateFunctions';
import {
  MyEditor,
  MyElement,
  MyRootBlock,
  MyValueTypes,
} from 'lib-knowledge-document-structure/plateTypes';
import {
  BaseSelection,
  Path,
} from 'lib-knowledge-document-structure/slate/package';

export type ActionFunctionParams = {
  editor: MyEditor;
  path: Path;
  selection: BaseSelection | null;
  element: MyRootBlock;
};

export type MenuHeading = {
  key: 'heading';
  label: string;
};

/** Block action menu option */
export type MenuOption =
  | {
      key: string;
      icon?: IconNames;
      className?: string;
      label: string;
      isSelected: boolean;
      action: (params: ActionFunctionParams) => void;
    }
  | MenuHeading;

export function isMenuHeading(menuItem: MenuOption): menuItem is MenuHeading {
  return menuItem.key === 'heading';
}

/** Duplicate and delete actions */
export const basicBlockActions: ReadonlyArray<MenuOption> = [
  {
    key: 'delete',
    icon: 'trash',
    label: 'Delete',
    isSelected: false,
    action: deleteBlock,
  },
  {
    key: 'duplicate',
    icon: 'copy',
    label: 'Duplicate',
    isSelected: false,
    action: duplicateBlock,
  },
];

/** Change a block from one type to another */
export const transformBlock =
  (to: MyValueTypes) =>
  ({ element, editor, path }: ActionFunctionParams): void => {
    if (to === element.type) return;
    setNodes(editor, { type: to }, { at: path });
  };

/** Transform actions */
export function getTransformBlockActions(
  currentType: MyValueTypes,
): ReadonlyArray<MenuOption> {
  const transformOptions = [
    { key: 'heading', label: 'Convert to' },
    {
      key: 'h_lg',
      label: 'Large Heading',
      icon: 'heading-1',
      action: transformBlock('h_lg'),
    },
    {
      key: 'h_md',
      label: 'Medium Heading',
      icon: 'heading-2',
      action: transformBlock('h_md'),
    },
    {
      key: 'h_sm',
      label: 'Small Heading',
      icon: 'heading-3',
      action: transformBlock('h_sm'),
    },
    {
      key: 'p',
      label: 'Text',
      icon: 'format',
      action: transformBlock('p'),
    },
    {
      key: 'blockquote',
      label: 'Quote',
      icon: 'quote',
      action: transformBlock('blockquote'),
    },
    {
      key: 'callout',
      label: 'Callout',
      icon: 'callout',
      action: transformBlock('callout'),
    },
    {
      key: 'code_block',
      label: 'Code block',
      icon: 'code-block',
      action: transformBlock('code_block'),
    },
  ] as const;

  return transformOptions.map((b) => ({
    ...b,
    isSelected: b.key === currentType,
  }));
}

/** Copies an element and all it's children. Giving each a new id. */
export function duplicateBlock({
  path,
  editor,
  element,
}: ActionFunctionParams): void {
  const newElement = duplicateElement(element);
  insertNodes(editor, newElement, {
    at: path,
  });
}

function duplicateElement(element: MyElement): MyElement {
  const newElement: MyElement = {
    ...element,
    id: makeNodeId(),
    // eslint-disable-next-line
    // @ts-ignore
    children: element.children.map((c) =>
      isMyElement(c) ? duplicateElement(c) : c,
    ),
  };
  return newElement;
}

export function deleteBlock({ path, editor }: ActionFunctionParams): void {
  deleteText(editor, { at: path });
}

export function addNewBlockBelow({
  path,
  editor,
  element,
}: ActionFunctionParams): void {
  const deepestChildPath = getLastChildPath([element, path]);
  const deepestNode = getNode(editor, deepestChildPath);
  const offset =
    typeof deepestNode?.text == 'string' ? deepestNode.text.length : 0;

  triggerShortcutMenu({
    editor,
    point: { path: deepestChildPath, offset },
    newLine: true,
  });
}
